import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAgentChat } from '@/pages/aiagent/AgentChatProvider';
import { AgentChatView } from '@/pages/aiagent/AgentChatView';

/**
 * AI Agent 우측 도크.
 *
 * <h2>왜 도크인가</h2>
 *
 * AI Agent 가 전체 화면 탭뿐이던 때는, 에이전트가 「현장 조회 열기」를 안내하고 그 링크를 누르면
 * <b>대화가 통째로 사라졌다.</b> 안내할 수 있는 화면이 47개라 에이전트가 유능해질수록 이 이탈이
 * 잦아진다. 도크는 본문 옆에 서서 <b>링크를 눌러도 대화가 남게</b> 한다.
 *
 * <p>대화 상태는 {@link AgentChatProvider} 에 있고 본문은 탭과 같은 {@link AgentChatView} 다 —
 * 이 파일은 <b>껍데기만</b> 맡는다. 폭·제목 줄·닫기·「크게 보기」가 여기 일이다.
 *
 * <h2>폭</h2>
 *
 * 360px. <b>좌측 사이드바를 접지 않는다</b>는 전제 위에서 본문에 남길 폭을 먼저 정하고 남은 것을
 * 가져왔다 — 계산과 문턱(1440)은 {@link AppLayout} 의 {@code DOCK_MIN_WIDTH} 머리말에 있다.
 */
export function AiAgentDock({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { activate, activeSession, startNew } = useAgentChat();

  /**
   * 도크를 <b>열 때마다 새 대화</b>로 시작한다 (사용자 지시 2026-09-01).
   *
   * <h2>왜 이어가면 안 되나</h2>
   *
   * 이력은 매 턴 <b>통째로</b> 에이전트에 실려 간다(`toHistory` — 「아까 말한 그거」가 열한 번째
   * 턴에서도 통해야 하므로 자르지 않는다). 그래서 마이페이지에서 열어 「현장 조회 화면입니다」라는
   * 답을 한 번 받으면, 그 문장이 다음 턴 프롬프트에 남아 <b>화면을 새로 알려 줘도 모델이 앞말을
   * 이어 간다.</b> 사용자가 본 「계속 홈이라고 한다」가 이 자리다.
   *
   * <p>다른 화면에서 다시 여는 것은 <b>다른 일을 시작하는 것</b>이다. 앞 화면의 맥락을 끌고 가는
   * 편이 이득인 경우보다, 끌고 가서 어긋나는 경우가 많다.
   *
   * <h2>이어지는 것은 그대로다</h2>
   *
   * 도크를 <b>열어 둔 채</b> 화면을 옮기는 것은 이어진다 — 그것이 도크를 만든 이유이고, 여기서
   * 끊지 않는다. 끊는 것은 <b>닫았다가 다시 여는</b> 순간뿐이다.
   * 지난 대화는 탭의 목록에서 고르면 그대로 열린다.
   */
  useEffect(() => {
    if (!open) return;
    activate();
    startNew();
    // `open` 이 참으로 바뀌는 순간만 본다. `startNew` 를 의존성에 넣으면 그 함수가 다시 만들어질
    // 때마다 대화가 초기화된다 — 사용자가 말하는 도중에 지워지는 사고다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  return (
    <aside
      className="dock-panel fixed right-0 top-14 bottom-0 w-[360px] flex flex-col bg-white no-print"
      style={{ borderLeft: '1px solid var(--border)' }}
    >
      {/*
        버튼은 제목 줄에 둔다 — 좁아서 깨지더라도 자리를 옮기지 않는다.
        「크게 보기」는 탭으로 넘기는 문이다. 대화는 provider 에 있으므로 넘어가도 그 자리 그대로다.
      */}
      <div
        className="flex items-center gap-2 px-3 h-12 flex-shrink-0"
        style={{ borderBottom: '1px solid var(--border)' }}
      >
        <svg
          width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          style={{ color: 'var(--accent)', flexShrink: 0 }}
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        <h2 className="text-sm font-bold truncate min-w-0" style={{ color: 'var(--text-primary)' }}>
          {activeSession?.title ?? 'AI Agent'}
        </h2>

        <div className="ml-auto flex items-center gap-1 flex-shrink-0">
          <button onClick={startNew} className="p-1.5 rounded-lg hover:bg-slate-100" aria-label="새 대화" title="새 대화">
            <svg
              width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" style={{ color: 'var(--text-secondary)' }}
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
          {/*
            지난 대화 목록·큰 카드는 탭의 일이다. 도크에 목록까지 넣으면 360px 안에서 둘이 다툰다.

            ★ `keep` 을 함께 보낸다. 탭은 **열 때마다 새 대화**로 시작하는데(위 머리말과 같은
              이유), 여기서 넘어가는 것은 「여는 것」이 아니라 **보던 대화를 크게 펴는 것**이다.
              이 표시가 없으면 크게 보려던 대화가 그 순간 지워진다.
          */}
          <button
            onClick={() => navigate('/aiagent', { state: { keep: true } })}
            className="p-1.5 rounded-lg hover:bg-slate-100"
            aria-label="크게 보기"
            title="크게 보기 (지난 대화 목록)"
          >
            <svg
              width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" style={{ color: 'var(--text-secondary)' }}
            >
              <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
            </svg>
          </button>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100" aria-label="닫기" title="닫기">
            <svg
              width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" style={{ color: 'var(--text-secondary)' }}
            >
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <AgentChatView variant="dock" />
    </aside>
  );
}
